import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, ImageIcon, Twitter, Linkedin, Facebook, Instagram } from "lucide-react";

const PLATFORM_ICON = { twitter: Twitter, linkedin: Linkedin, facebook: Facebook, instagram: Instagram };

// Shows the EXACT caption + image that will publish, using the backend's own formatter.
export default function PublishPreviewModal({ open, onOpenChange, post }) {
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const platform = post?.platform || "facebook";
  const Icon = PLATFORM_ICON[platform] || Facebook;

  useEffect(() => {
    if (!open || !post) return;
    setLoading(true);
    setContent("");
    setImageUrl(null);
    api.post("/publish-preview", {
      platform,
      post_text: post.post_text || "",
      hashtags: post.hashtags || [],
      link_url: post.link_url || post.source_url || "",
      cta_text: post.cta_text || "",
      ad_style: !!post.ad_style,
      image_path: post.image_path || null,
    }).then((r) => {
      setContent(r.data?.content || "");
      setImageUrl(r.data?.image_url || (post.image_path ? `${API}/media/${post.image_path}` : null));
    }).catch(() => {
      setContent(post.post_text || "");
      setImageUrl(post.image_path ? `${API}/media/${post.image_path}` : null);
    }).finally(() => setLoading(false));
  }, [open, post]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="publish-preview-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-primary" /> Exactly what will publish
          </DialogTitle>
          <DialogDescription>
            This is the caption and image sent to {platform}, formatted by the publisher.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-16 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Building preview…
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="publish-preview-card">
            <div className="relative aspect-square bg-secondary/40 flex items-center justify-center">
              {imageUrl ? (
                <img src={imageUrl} alt="post graphic" className="w-full h-full object-cover" data-testid="publish-preview-image" />
              ) : (
                <div className="flex flex-col items-center text-muted-foreground gap-2">
                  <ImageIcon className="h-10 w-10" />
                  <span className="text-xs">No graphic — will post text-only</span>
                </div>
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2 text-muted-foreground">
                <Icon className="h-4 w-4" />
                <span className="text-xs font-mono uppercase tracking-wider">{platform}</span>
                <Badge variant="outline" className="ml-auto text-[10px] border-border">Live format</Badge>
              </div>
              <p className="text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed" data-testid="publish-preview-caption">
                {content || "(no caption)"}
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
